<script lang="ts" setup>
import type {
  PdpEcommerceBlockProductDetailsRedesignProps,
  SkuSize,
} from './PdpEcommerceBlockProductDetailsRedesign.props'
import type { ColorOption } from '@design-system/components/Pdp/PdpColorSelector.props'
import type { Size, extSize } from '../../PdpSizeSelector.props'
import { SIDEBAR_IDS } from '@design-system/data/sidebarIds'
import type { SingleSizeDisplay } from '@design-system/utils/microCategories'
import { getSingleSizeDisplay } from '@design-system/utils/microCategories'
import UIFinalSales from '@design-system/components/UI/UIFinalSales.vue'
import UIFriendsFamily from '@design-system/components/UI/UIFriendsFamily.vue'
const storeLocatorIcon = '/store-locator-icon.svg'

const { ts } = useI18n()
const { addToCart } = useAddToCart()
const { isPdpFastCheckoutHidden } = useFastCheckoutIntent()

const props = withDefaults(
  defineProps<PdpEcommerceBlockProductDetailsRedesignProps>(),
  {
    selectedExtSku: '',
    thisProductDims: '',
  }
)

const emit = defineEmits<{
  'update:selectedSizeIndex': [selectedSizeIndex: number | null]
  'update:selectedCountry': [selectedCountry: string]
  'update:selectedExtSku': [selectedExtSku: string]
  'similar-products': []
  openSidebar: [sidebarId: string]
  addToCart: []
  'update-wishlist': []
  'tag-click': [tag: string]
  'hover-color': [option: ColorOption | null]
  'select-color': [option: ColorOption]
}>()

const { isDialogOpen: isSizeSelectorDialogOpen } = useDialog(
  SIDEBAR_IDS.pdpEcommerceBlockSizeSelector
)
const similarProductState = reactive({
  similarProductClicked: false,
  isSidebarClosed: false,
})

const { openDialog } = useDialog()
const { getCanonicalUrl, storeLocator } = useRouteHelper()
const { isShoppable } = useIsShoppable()
const { marketDefaultShippingMethod } = useShippingMethods()
const { dispatchFitAnalyticsEvent } = useGANavigationEvents()
const { dispatchActionReserveInStoreEvent } = useGAActionEvents()

const hideForCustomers = props.hideForCustomers ?? false

const lastInStock = computed(() => {
  if (!props.sizes[props.selectedCountry]) return false

  // CL check
  const sizes = props.sizes[props.selectedCountry]
  const disabledCount = sizes.filter((item: Size) => item.disabled).length
  const lastInStockCount = sizes.filter(
    (item: Size) => item.lastInStock && item.disabled === false
  ).length

  // algolia check
  const skuSizes = props.skuSizes
  const disabledCountAlgolia = skuSizes.filter(
    (item: SkuSize) => item.avEcom === 0 && item.avRet === 0
  ).length
  const lastInStockCountAlgolia = skuSizes.filter(
    (item: SkuSize) => item.avEcom === 1 || item.avRet === 1
  ).length

  // to be last in stock all sizes but one have to be disabled (outOfStock) and only one size has to be lastInStock
  return (
    (disabledCount === sizes.length - 1 && lastInStockCount === 1) ||
    (disabledCountAlgolia === skuSizes.length - 1 &&
      lastInStockCountAlgolia === 1)
  )
})

const onlyStoreMobile = useState<boolean | undefined>('onlyStoreMobile')

const onlyStore = computed(() => {
  const res =
    props.skuSizes?.every(item => item.avEcom === 0) &&
    props.skuSizes?.some(item => item.avRet && item.avRet > 0)
  onlyStoreMobile.value = res
  return res
})

const outOfStock = computed(
  () =>
    props.skuSizes?.every(item => item.avEcom === 0 && item.avRet === 0) &&
    props.sizes[props.selectedCountry]?.every(item => item.disabled)
)

const oneSize = computed(() => {
  if (!props.sizes[props.selectedCountry]) {
    return false
  }
  const firstSize = props.sizes?.[props.selectedCountry]?.[0]?.['sizeCode']
  return (
    ((firstSize === 'TU' || firstSize === 'ONESIZE') &&
      (!props.extSizes || props.extSizes.length === 0)) ||
    props.isSingleSizeByMicroCat
  )
})

const singleSizeDisplay = computed<SingleSizeDisplay>(() => {
  if (!props.isSingleSizeByMicroCat) return {}
  return getSingleSizeDisplay(props.microCatKind) ?? {}
})

const hideColorBlock = computed(() => !!singleSizeDisplay.value.hideColorBlock)

const showSizeInfoButton = computed(
  () => !!singleSizeDisplay.value.sizeInfoLabelKey
)

const sizeInfoLabel = computed(() => {
  const labelKey = singleSizeDisplay.value.sizeInfoLabelKey
  const firstSize = props.sizes?.[props.selectedCountry]?.[0]
  const sizeCode = firstSize?.sizeCode ?? firstSize?.size
  if (!labelKey || !sizeCode) return undefined
  return `${ts(labelKey)} ${sizeCode}`
})

const oneSizeLabel = computed(() => {
  if (!singleSizeDisplay.value.asColorAsLabel) return undefined
  return props.algoliaColor?.trim() || undefined
})

const sizeLabelOverride = computed(() =>
  singleSizeDisplay.value.sizeLabelKey
    ? ts(singleSizeDisplay.value.sizeLabelKey)
    : undefined
)

const { getDims } = await usePdpExtSizesUtils()

const productRoute = props.productRoute ?? ''

const dims = computed(() => {
  return getDims(
    props.selectedExtSku,
    props.extSizes ?? [],
    props.thisProductDims,
    productRoute,
    props.skuSizes,
    oneSize.value && !extSizesCheck.value
  )
})

const selectedSizeState = useState<extSize>('selectedSizeState')

const selectedSize = computed(() => {
  if (!props.sizes[props.selectedCountry]) return 0

  // one size
  if (oneSize.value && !props.extSizes) return 0
  // ext sizes
  if (props.extSizes && props.extSizes.length > 0) {
    return selectedSizeState?.value?.value ?? 0
  }
  // default
  return props.selectedSizeIndex
})

const fastCheckoutSelectedSizeLabel = computed(() => {
  if (props.extSizes && props.extSizes.length > 0) {
    return selectedSizeState.value?.label ?? ''
  }
  if (oneSize.value) {
    return oneSizeLabel.value ?? ts('pdp.sizeSelector.oneSize')
  }
  if (props.selectedSizeIndex === null) return ''
  const size = props.sizes[props.selectedCountry]?.[props.selectedSizeIndex]
  return size?.size ? `${size.size} (${props.selectedCountry})` : ''
})

const outOfStockExtSizes = computed(() => {
  if (!props.extSizes) return false
  const totalAvEcom = props.extSizes.reduce(
    (acc, product) => acc + (product.avEcom ?? 0),
    0
  )
  const totalAvRet = props.extSizes.reduce(
    (acc, product) => acc + (product.avRet ?? 0),
    0
  )
  const CLAllExtOos = props.extSizes.every(
    (item: extSize) => item.outOfStock === true
  )
  if (
    (totalAvEcom + totalAvRet === 0 && props.extSizes.length > 0) ||
    CLAllExtOos
  ) {
    return true
  }
  return false
})

const _addToCart = async () => {
  await addToCart()
  emit('addToCart')
}

const clickAddToCart = async (id: string) => {
  if (
    (props.selectedSizeIndex !== null && props.selectedSizeIndex >= 0) ||
    extSizesCheck.value
  ) {
    await _addToCart()
    if (!oneSize.value) emit('update:selectedSizeIndex', null)
  } else {
    openDialogIfNotDisabled(id)
  }
}

const openDialogIfNotDisabled = (id: string) => {
  if (
    (props.extSizes?.length === 0 && !outOfStock.value) ||
    (props.extSizes && props.extSizes.length > 0 && !outOfStockExtSizes.value)
  )
    openDialog(id)
}
const selectColor = (selectedProductColor: ColorOption) => {
  emit('select-color', selectedProductColor)
  const path = getCanonicalUrl(selectedProductColor.path)
  navigateTo(path)
}

const isExtSizeOrOneSizeFitFinder = computed(
  () => !!extSizesCheck.value || !!oneSize.value
)

const extSizesCheck = computed(() => {
  const res = props.extSizes && props.extSizes.length > 0
  return res
})
const selectedExtOutOfStockMobile = useState<boolean | undefined>(
  'selectedExtOutOfStockMobile'
)

const selectedExtOutOfStock = computed(() => {
  if (props.extSizes && props.extSizes.length > 0) {
    const myObj = props.extSizes.find(
      (obj: extSize) =>
        obj.SKU === props.selectedExtSku && obj.Dims === dims.value
    )
    selectedExtOutOfStockMobile.value = myObj?.outOfStock ?? false
    return myObj?.outOfStock
  }
})

const selectedDefaultOrOneSizeOutOfStockCl = computed(() => {
  if (extSizesCheck.value) return false
  if (oneSize.value) {
    return (
      props.sizes[`${props.selectedCountry}`][0]['disabled'] ||
      (props.skuSizes[0].avRet === 0 && props.skuSizes[0].avEcom === 0)
    )
  }
  if (
    props.sizes[`${props.selectedCountry}`].every(
      (item: Size) => item.disabled === true
    ) ||
    props.skuSizes?.every(
      (item: SkuSize) => item.avEcom === 0 && item.avRet === 0
    )
  ) {
    return true
  } else {
    return false
  }
})

const disabledMobileAddToCart = useState<boolean | undefined>(
  'disabledMobileAddToCart'
)

const allExtSizesOosOrDefaultSizesOos = computed(() => {
  const res =
    (selectedDefaultOrOneSizeOutOfStockCl.value && !extSizesCheck.value) ||
    (outOfStockExtSizes.value && extSizesCheck.value)
  disabledMobileAddToCart.value = (res || props.isFallback) ?? false
  return res
})

const handleReserveClick = () => {
  emit('openSidebar', SIDEBAR_IDS.wrapperReserveInStore)
  dispatchActionReserveInStoreEvent(
    props.title,
    selectedSize.value
      ? props.skuSizes[Number(props?.selectedSizeIndex)].label!
      : '',
    props.selectedColor,
    props.selectedExtSku
  )
}

const isCasa = computed(() => {
  return props.brand === 'ARMANI CASA'
})
const isInfoCommerceProduct = computed(() => {
  return isCasa.value && props.infoCommerce
})

const showStoreLocator = computed(() => isCasa.value || !isShoppable.value)

/* Sticky add to cart bar, shown on desktop while scrolling the lower part of the pdp */
const showStickyBar = computed(
  () =>
    !!props.isStickyBarVisible &&
    !!isShoppable.value &&
    !onlyStore.value &&
    !allExtSizesOosOrDefaultSizesOos.value &&
    !isInfoCommerceProduct.value &&
    !props.isFallback
)

const isStickyBarCtaDisabled = computed(
  () =>
    (outOfStock.value && oneSize.value) ||
    (outOfStockExtSizes.value && extSizesCheck.value) ||
    !!selectedExtOutOfStock.value ||
    selectedDefaultOrOneSizeOutOfStockCl.value
)

const stickyBarImage = computed(
  () =>
    props.orderedGallery?.find(item => item.type === 'TResponsiveImage')
      ?.data as string | undefined
)

const baaUrl = computed(() => {
  return `/armani-casa/experience/book-an-appointment/`
})

const emitActionAside = () => {
  if (showStoreLocator.value) {
    const path = getCanonicalUrl(storeLocator)
    navigateTo(path, {
      external: true,
    })
  } else {
    emit('similar-products')
  }
}

const resetSimilarProductState = () => {
  similarProductState.isSidebarClosed = false
  similarProductState.similarProductClicked = false
}

watch(
  [similarProductState, isSizeSelectorDialogOpen],
  ([state, dialogOpen]) => {
    if (!dialogOpen && state.isSidebarClosed && state.similarProductClicked) {
      emit('similar-products')
      resetSimilarProductState()
    }
    if (dialogOpen) {
      resetSimilarProductState()
    }
  }
)
</script>

<template>
  <div class="xl:pt-7xl lg:pt-3xl flex w-full flex-col justify-between">
    <div
      class="title-price-block flex flex-col gap-8 xl:gap-10"
      :class="{
        'gap-xl lg:gap-lg xl:gap-4xl': !hideDetails,
      }"
    >
      <div class="flex flex-col gap-6">
        <PdpEcommerceBlockProductNameRedesign
          :product-code="productId"
          :class="{ 'hidden lg:block': !hideDetails }"
          :title="title"
          :price="price"
          :old-price="oldPrice"
          :discount="discount"
          :tags="tags"
          :is-shoppable="!!isShoppable && !isInfoCommerceProduct"
          :is-heading="isHeading"
          :is-last-in-stock="lastInStock"
          :hide-details="hideDetails"
          @update-wishlist="emit('update-wishlist')"
          @tag-click="emit('tag-click', $event)"
        />
        <PdpEcommerceBlockColorSelectorInline
          v-if="!isInfoCommerceProduct && !hideColorBlock"
          :sku-code="productId"
          :options="colorOptions"
          :selected-color="selectedColor"
          :selected-color-hex="selectedColorHex"
          :ext-colors="extColors"
          :color-hex="colorHex"
          has-product-actions
          @update:selected-option="selectColor"
          @update-wishlist="emit('update-wishlist')"
          @hover-option="emit('hover-color', $event)"
        />
      </div>
      <div
        v-if="
          !onlyStore &&
          (allExtSizesOosOrDefaultSizesOos ||
            selectedExtOutOfStock ||
            isRetailExclusive ||
            isInfoCommerceProduct ||
            isFallback)
        "
      >
        <AtomsBadgeProductCart
          v-if="isShoppable"
          :key="'pdp-badge-product-card-only-store'"
          :text="$ts('pdp.productDetails.informationSection.onlyStore')"
        />
      </div>
      <div
        v-if="
          isShoppable &&
          !onlyStore &&
          !allExtSizesOosOrDefaultSizesOos &&
          !isInfoCommerceProduct &&
          !isFallback
        "
        class="gap-md flex flex-col"
      >
        <div class="flex flex-col gap-10">
          <div class="flex flex-col gap-3">
            <div
              v-if="!isExtSizeOrOneSizeFitFinder || showSizeInfoButton"
              class="flex w-full"
            >
              <PdpEcommerceBlockFitFinderRedesign
                :hide-sidebar="hideDetails"
                :size-code-id="sizeGridCode"
                :main-category="mainCategory"
                :product-title="title"
                :product-id="productId"
                :side-slide-description="sideSlideDescription"
                :custom-label="showSizeInfoButton ? sizeInfoLabel : undefined"
                :fitting="fitting"
                @on-fit-finder-click="dispatchFitAnalyticsEvent"
              />
            </div>
            <PdpEcommerceBlockSizeSelectorRedesign
              :hide-sidebar-component="hideDetails"
              :selected-size-index="selectedSize"
              :selected-country="selectedCountry"
              :sizes="sizes"
              :sku-sizes="skuSizes"
              :ext-sizes="extSizes"
              :selected-ext-sku="selectedExtSku"
              :side-slide-description="sideSlideDescription"
              :is-one-size="oneSize"
              :has-size-info-button="showSizeInfoButton"
              :one-size-label="oneSizeLabel"
              :size-label-override="sizeLabelOverride"
              :dims="dims"
              :out-of-stock="outOfStockExtSizes"
              :disabled="outOfStock && oneSize"
              :selected-ext-out-of-stock="selectedExtOutOfStock"
              :selected-default-or-one-size-out-of-stock-cl="
                selectedDefaultOrOneSizeOutOfStockCl
              "
              @send-add-to-cart="_addToCart"
              @update:selected-size-index="
                emit('update:selectedSizeIndex', $event)
              "
              @update:selected-country="emit('update:selectedCountry', $event)"
              @update:selected-ext-sku="emit('update:selectedExtSku', $event)"
              @similar-products="
                similarProductState.similarProductClicked = true
              "
              @sidebar-closed-after-leave="
                similarProductState.isSidebarClosed = true
              "
            />
          </div>

          <div>
            <PdpEcommerceBlockProductAddToCartButton
              full-width
              :is-loading="addToCartProps.isLoading"
              :error="addToCartProps.error"
              :selected-country="selectedCountry"
              :is-one-size="oneSize"
              :disabled="outOfStock && oneSize"
              :out-of-stock="outOfStockExtSizes && extSizesCheck"
              :selected-ext-out-of-stock="selectedExtOutOfStock"
              :selected-default-or-one-size-out-of-stock-cl="
                selectedDefaultOrOneSizeOutOfStockCl
              "
              class="max-h-[52px]"
              @send-add-to-cart="
                clickAddToCart(SIDEBAR_IDS.pdpEcommerceBlockSizeSelector)
              "
              @update:selected-size-index="
                emit('update:selectedSizeIndex', $event)
              "
              @update:selected-country="emit('update:selectedCountry', $event)"
            />
            <PdpEcommerceBlockProductFastCheckout
              v-if="!hideDetails && !isPdpFastCheckoutHidden"
            />
            <PdpEcommerceBlockProductFastCheckoutSideSlide
              v-if="!hideDetails && !isPdpFastCheckoutHidden"
              :product="props"
              :image="stickyBarImage"
              :product-id="productId"
              :selected-size-label="fastCheckoutSelectedSizeLabel"
            />
            <div class="mt-6 flex flex-wrap justify-between gap-2 md:mt-3">
              <UtilsMarkdown
                v-if="marketDefaultShippingMethod?.pdpDescription"
                class="text-light-7"
                :content="marketDefaultShippingMethod?.pdpDescription"
              />

              <AtomsCta
                anatomy="link"
                :aria-label="
                  $ts('pdp.productDetails.informationSection.payments')
                "
                icon-position="left"
                class="text-link-7 cursor-pointer"
                @click="emit('openSidebar', SIDEBAR_IDS.pdpInfoPaymentsItem)"
              >
                <template #icon>
                  <DefaultIconsInfo class="h-6 w-6" aria-hidden="true" />
                </template>
                <template #label>
                  {{ $ts('pdp.productDetails.informationSection.payments') }}
                </template>
              </AtomsCta>
            </div>
            <UIFinalSales
              v-if="finalSales === 'Y'"
              is-new-pdp
              class="mt-3"
              :sidebar-id="SIDEBAR_IDS.pdpInfoReturnItem"
            />
          </div>
        </div>
        <div
          v-if="
            !hideDetails &&
            ((isFriendsFamily && hideForCustomers) || props.pdpMessage)
          "
          class="gap-xs flex flex-col"
        >
          <UIFriendsFamily
            v-if="isFriendsFamily && hideForCustomers"
            icon="group"
            :is-pdp-label="true"
            :sidebar-id="SIDEBAR_IDS.pdpFriendsFamily"
          />
          <AtomsMessageInfo
            v-if="props.pdpMessage"
            icon="tailoring"
            :text="$ts(`${props.pdpMessage.label}`)"
          >
            <template #dynamicContent>
              <button
                :aria-label="$ts('accessibility.pdpMessage')"
                @click="emit('openSidebar', SIDEBAR_IDS.pdpMessageSideSlide)"
              >
                <DefaultIconsInfo class="h-4 w-4" aria-hidden="true" />
              </button>
            </template>
          </AtomsMessageInfo>
        </div>
      </div>
      <div
        v-if="
          (!onlyStore &&
            !allExtSizesOosOrDefaultSizesOos &&
            !hideDetails &&
            !isInfoCommerceProduct &&
            !hideForCustomers) ||
          isRetailExclusive
        "
      >
        <AtomsCta
          anatomy="link"
          class="text-link-7"
          aria-haspopup="dialog"
          icon-position="left"
          @click="handleReserveClick"
        >
          <template #icon>
            <img
              :src="storeLocatorIcon"
              :alt="$ts('pdp.productDetails.informationSection.reserve')"
            />
          </template>
          <template #label>
            {{ $ts('pdp.productDetails.informationSection.reserve') }}
          </template>
        </AtomsCta>
      </div>
      <div
        v-if="
          onlyStore &&
          !hideDetails &&
          !isInfoCommerceProduct &&
          !hideForCustomers
        "
        class="max-md:hidden"
      >
        <AsideButton
          class="max-h-[52px]"
          data-testid="size-selector"
          :centered-btn="onlyStore"
          @click="emit('openSidebar', SIDEBAR_IDS.wrapperReserveInStore)"
        >
          <span class="text-light-6 flex items-center gap-1">
            <img
              :src="storeLocatorIcon"
              :alt="$ts('pdp.productDetails.informationSection.reserve')"
            />
            {{ $ts('pdp.productDetails.informationSection.reserve') }}
          </span>
        </AsideButton>
      </div>
      <!-- Out of stock CTA: only when online stock = 0 AND find in store stock = 0 -->
      <div
        v-if="
          (isInfoCommerceProduct ||
            (allExtSizesOosOrDefaultSizesOos && !onlyStore)) &&
          !hideDetails
        "
      >
        <div v-if="isInfoCommerceProduct">
          <div class="flex flex-col items-start gap-6 pb-6">
            <div class="text-light-6 line-clamp-3">
              <!--
              Commentato temporaneamente per non mostrare la sezione
              <p>
                {{
                  $ts(
                    'pdp.productDetails.informationSection.armaniCasa.findDetails'
                  )
                }}
                <MoleculesLinkWrapper
                  :to="props.productPdf !== '' ? props.productPdf : '#'"
                  target="_blank"
                  rel="noopener noreferrer"
                  anatomy="link"
                >
                  <template #label>
                    {{
                      $ts(
                        'pdp.productDetails.informationSection.armaniCasa.dataSheet'
                      )
                    }}
                  </template>
                </MoleculesLinkWrapper>
              </p> -->
            </div>
            <div class="text-light-6 line-clamp-3">
              <p>
                {{
                  $ts(
                    'pdp.productDetails.informationSection.armaniCasa.pleaseVisit'
                  )
                }}
                <MoleculesLinkWrapper
                  :to="storeLocator"
                  anatomy="link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <template #label>
                    {{
                      $ts(
                        'pdp.productDetails.informationSection.armaniCasa.storeLocator'
                      )
                    }}
                  </template>
                </MoleculesLinkWrapper>
                {{
                  $ts(
                    'pdp.productDetails.informationSection.armaniCasa.storeLocatorSentance'
                  )
                }}
              </p>
            </div>
            <div class="text-light-6 line-clamp-3">
              <p>
                {{
                  $ts(
                    'pdp.productDetails.informationSection.armaniCasa.bookYour'
                  )
                }}
                <MoleculesLinkWrapper
                  :to="baaUrl"
                  anatomy="link"
                  :title="'in-store appointment'"
                  external
                >
                  <template #label>
                    {{
                      $ts(
                        'pdp.productDetails.informationSection.armaniCasa.appointment'
                      )
                    }}
                  </template>
                </MoleculesLinkWrapper>
                {{
                  $ts(
                    'pdp.productDetails.informationSection.armaniCasa.appointmentSentence'
                  )
                }}
              </p>
            </div>
          </div>
        </div>
        <AsideButton
          class="max-h-[52px]"
          :centered-btn="
            outOfStock ||
            outOfStockExtSizes ||
            selectedDefaultOrOneSizeOutOfStockCl
          "
          data-testid="button-out-of-stock"
          @click="emitActionAside"
        >
          <span class="text-light-6">
            {{
              showStoreLocator
                ? $ts('pdp.sizeSelector.storeLocatorCta')
                : $ts('pdp.sizeSelector.emptyStock')
            }}
          </span>
        </AsideButton>
      </div>
    </div>
    <PdpInfoPayments :price="price" :price-cents="priceCents" />
    <PdpEcommerceBlockStickyBar
      :is-visible="showStickyBar"
      :title="title"
      :image="stickyBarImage"
      :price="price"
      :old-price="oldPrice"
      :discount="discount"
      :is-loading="addToCartProps.isLoading"
      :is-disabled="isStickyBarCtaDisabled"
      @add-to-cart="clickAddToCart(SIDEBAR_IDS.pdpEcommerceBlockSizeSelector)"
    />
  </div>
</template>
